import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as Sentry from '@sentry/react';
import type { Attachment } from '@sentry/core';
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogContent,
    Menu,
    MenuItem,
    Portal,
    Stack,
    Tab,
    Tabs,
    TextField,
    Typography,
} from '@mui/material';
import useNotificationStore from '@gui/store/notification';
import { NotificationType } from '@visualizer/types';
import usePersistentStore from '@gui/store/persistent/persistentStore';
import { ScreenshotExporter, ScreenshotResolution } from '../../../../Utils/ScreenshotExporter';
import { getUserInfo } from 'sentry';
import { useScreenshotSelection } from './useScreenshotSelection';

interface DialogSizing {
    showContent: boolean;
    onFeedbackSent: () => void;
}

const SUPPORT_FORM_URL = 'https://share-eu1.hsforms.com/2aMHmwkzORYOpIHazwsv4kgfggyx';
type ReportIssueTab = 'report-bug' | 'request-support';

const ReportIssue: React.FC<DialogSizing> = ({ showContent, onFeedbackSent }) => {
    const showNotification = useNotificationStore((s) => s.showNotification);
    const [message, setMessage] = useState('');
    const [sending, setSending] = useState(false);
    const [iframeLoading, setIframeLoading] = useState(true);
    const [supportModalOpen, setSupportModalOpen] = useState(false);
    const [activeTab, setActiveTab] = useState<ReportIssueTab>('report-bug');
    const [screenshotFile, setScreenshotFile] = useState<File | null>(null);
    const [screenshotMenuAnchor, setScreenshotMenuAnchor] = useState<HTMLElement | null>(null);
    const fileInputRef = useRef<HTMLInputElement | null>(null);

    const supportFormUrl = useMemo(() => {
        if (typeof window === 'undefined') return SUPPORT_FORM_URL;

        const rawUser = localStorage.getItem('user');
        if (!rawUser) return SUPPORT_FORM_URL;

        try {
            const parsedUser = JSON.parse(rawUser) as { email?: string };
            const email = parsedUser?.email?.trim();
            if (!email) return SUPPORT_FORM_URL;

            const url = new URL(SUPPORT_FORM_URL);
            url.searchParams.set('email', email);
            return url.toString();
        } catch {
            return SUPPORT_FORM_URL;
        }
    }, []);

    const getStateAttachment = useCallback((): Attachment | null => {
        const storageName = usePersistentStore.persist.getOptions().name;
        if (!storageName) return null;
        const raw = localStorage.getItem(storageName);
        if (!raw) return null;

        let payload = raw;
        try {
            payload = JSON.stringify(JSON.parse(raw), null, 2);
        } catch {
            // keep raw if parsing fails
        }

        return {
            data: payload,
            filename: 'state.json',
            contentType: 'application/json',
        };
    }, []);

    const captureCanvasScreenshotAttachment = useCallback(async (): Promise<Attachment | null> => {
        try {
            const exporter = new ScreenshotExporter();
            const data = await exporter.getScreenshot(ScreenshotResolution.HD, true, 'png');
            if (!data || data.length === 0) return null;

            return {
                data: new Uint8Array(data),
                filename: 'screenshot.png',
                contentType: 'image/png',
            };
        } catch (error) {
            console.warn('Unable to capture screenshot for feedback.', error);
            return null;
        }
    }, []);

    const fileToAttachment = useCallback(async (file: File): Promise<Attachment> => {
        const bytes = new Uint8Array(await file.arrayBuffer());
        return {
            data: bytes,
            filename: file.name || 'screenshot.png',
            contentType: file.type || 'image/png',
        };
    }, []);

    const { selectingScreenshot, selectionRect, startSelection, cancelSelection, captureSelection, handleMouseDown } =
        useScreenshotSelection({
            onCapture: setScreenshotFile,
            onError: (notificationMessage) => showNotification(notificationMessage, NotificationType.Error),
        });

    const handleSubmit = useCallback(async () => {
        if (!Sentry.isEnabled()) {
            showNotification('Sentry is disabled.', NotificationType.Error);
            return;
        }

        if (sending) return;
        setSending(true);

        try {
            const [manualScreenshot, automaticScreenshot, stateAttachment] = await Promise.all([
                screenshotFile ? fileToAttachment(screenshotFile) : Promise.resolve(null),
                captureCanvasScreenshotAttachment(),
                Promise.resolve(getStateAttachment()),
            ]);

            if (!automaticScreenshot || !stateAttachment) {
                showNotification('Unable to attach the automatic screenshot and state. Please try again.', NotificationType.Error);
                return;
            }

            const attachments = manualScreenshot
                ? [automaticScreenshot, manualScreenshot, stateAttachment]
                : [automaticScreenshot, stateAttachment];

            const trimmedMessage = message.trim();
            const feedbackMessage = trimmedMessage || 'No message provided.';
            const userInfo = getUserInfo();
            const eventId = Sentry.captureFeedback(
                {
                    message: feedbackMessage,
                    name: userInfo.username,
                    email: userInfo.email,
                    url: window.location.href,
                },
                {
                    includeReplay: true,
                    attachments,
                }
            );

            console.info('Sentry Feedback Submitted', {
                eventId,
                hasMessage: trimmedMessage.length > 0,
                automaticScreenshotBytes: automaticScreenshot.data instanceof Uint8Array ? automaticScreenshot.data.byteLength : undefined,
                manualScreenshotBytes: manualScreenshot?.data instanceof Uint8Array ? manualScreenshot.data.byteLength : undefined,
                stateBytes: typeof stateAttachment.data === 'string' ? stateAttachment.data.length : undefined,
            });

            setMessage('');
            setScreenshotFile(null);
            setScreenshotMenuAnchor(null);
            showNotification('Thanks! Your feedback has been sent.', NotificationType.Success);
            onFeedbackSent();
        } catch (error) {
            console.error(error);
            showNotification('Failed to send feedback.', NotificationType.Error);
        } finally {
            setSending(false);
        }
    }, [
        captureCanvasScreenshotAttachment,
        fileToAttachment,
        getStateAttachment,
        message,
        onFeedbackSent,
        screenshotFile,
        sending,
        showNotification,
    ]);

    const preventKeyPropagation = useCallback((event: React.KeyboardEvent) => {
        event.stopPropagation();
    }, []);

    const openSupportModal = useCallback(() => {
        setIframeLoading(true);
        setSupportModalOpen(true);
    }, []);

    const closeSupportModal = useCallback(() => {
        setSupportModalOpen(false);
    }, []);

    const handleScreenshotChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            const file = event.target.files?.[0] || null;
            if (!file) return;
            if (!file.type.startsWith('image/')) {
                showNotification('Please select an image file.', NotificationType.Error);
                event.target.value = '';
                return;
            }

            setScreenshotFile(file);
            setScreenshotMenuAnchor(null);
            event.target.value = '';
        },
        [showNotification]
    );

    const handleOpenScreenshotMenu = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        setScreenshotMenuAnchor((current) => (current ? null : event.currentTarget));
    }, []);

    const handleCloseScreenshotMenu = useCallback(() => {
        setScreenshotMenuAnchor(null);
    }, []);

    const handlePickLocalScreenshot = useCallback(() => {
        handleCloseScreenshotMenu();
        fileInputRef.current?.click();
    }, [handleCloseScreenshotMenu]);

    const handleCancelAreaSelection = useCallback(() => {
        cancelSelection();
    }, [cancelSelection]);

    const handleStartAreaSelection = useCallback(() => {
        handleCloseScreenshotMenu();
        startSelection();
    }, [handleCloseScreenshotMenu, startSelection]);

    useEffect(() => {
        if (!showContent) {
            setScreenshotMenuAnchor(null);
            cancelSelection();
            return;
        }

        setActiveTab('report-bug');
    }, [cancelSelection, showContent]);

    const handleTabChange = useCallback(
        (_: React.SyntheticEvent, nextTab: ReportIssueTab) => {
            if (nextTab === 'request-support') {
                setActiveTab('report-bug');
                openSupportModal();
                onFeedbackSent();
                return;
            }

            setActiveTab(nextTab);
        },
        [onFeedbackSent, openSupportModal]
    );

    return (
        <>
            {showContent && (
                <Box>
                    <Tabs
                        value={activeTab}
                        onChange={handleTabChange}
                        variant="fullWidth"
                        sx={{
                            mt: 1,
                            minHeight: 36,
                            '& .MuiTab-root': {
                                minHeight: 36,
                                minWidth: 0,
                                px: 0.25,
                                py: 0.5,
                                borderRadius: '8px 8px 0 0',
                                fontSize: 10,
                                fontWeight: 600,
                                whiteSpace: 'nowrap',
                            },
                        }}
                    >
                        <Tab value="report-bug" label="REPORT BUG" />
                        <Tab value="request-support" label="REQUEST SUPPORT" />
                    </Tabs>
                    {activeTab === 'report-bug' && (
                        <Stack spacing={1.5} mt={1.5}>
                            <Typography variant="body2" color="text.secondary">
                                Describe the problem you encountered.
                            </Typography>
                            <TextField
                                value={message}
                                onChange={(event) => setMessage(event.target.value)}
                                onKeyDown={preventKeyPropagation}
                                onKeyUp={preventKeyPropagation}
                                size="small"
                                fullWidth
                                multiline
                                minRows={5}
                                placeholder="What happened? Steps to reproduce? Expected result?"
                            />
                            <Box className="report-issue-upload-row">
                                <Box className="report-issue-screenshot-picker">
                                    <Button
                                        className="report-issue-screenshot-trigger"
                                        variant="outlined"
                                        size="small"
                                        onClick={handleOpenScreenshotMenu}
                                    >
                                        {screenshotFile ? 'Replace screenshot' : 'Add screenshot'}
                                    </Button>
                                    <Menu
                                        anchorEl={screenshotMenuAnchor}
                                        open={Boolean(screenshotMenuAnchor)}
                                        onClose={handleCloseScreenshotMenu}
                                        keepMounted
                                        sx={{ zIndex: 11001 }}
                                    >
                                        <MenuItem onClick={handlePickLocalScreenshot}>Upload from computer</MenuItem>
                                        <MenuItem onClick={handleStartAreaSelection}>Capture screenshot</MenuItem>
                                    </Menu>
                                </Box>
                                <input ref={fileInputRef} type="file" accept="image/*" hidden onChange={handleScreenshotChange} />
                                {screenshotFile && (
                                    <>
                                        <Typography variant="caption" className="report-issue-upload-name">
                                            {screenshotFile.name}
                                        </Typography>
                                        <Button variant="text" size="small" onClick={() => setScreenshotFile(null)}>
                                            Remove
                                        </Button>
                                    </>
                                )}
                            </Box>
                            <Box className="report-issue-submit-row">
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={() => void handleSubmit()}
                                    disabled={sending || message.trim().length === 0}
                                >
                                    {sending ? <CircularProgress size={16} color="inherit" /> : 'Submit'}
                                </Button>
                            </Box>
                        </Stack>
                    )}
                </Box>
            )}
            <Dialog
                open={supportModalOpen}
                onClose={closeSupportModal}
                maxWidth={false}
                classes={{ paper: 'report-issue-support-dialog-paper' }}
                sx={{ zIndex: 11002 }}
            >
                <DialogContent className="report-issue-support-dialog-content">
                    <Stack className="report-issue-support-dialog-stack" spacing={0}>
                        <Box className="report-issue-iframe-shell">
                            {iframeLoading && (
                                <Box className="report-issue-iframe-loader">
                                    <CircularProgress size={24} />
                                </Box>
                            )}
                            <Box
                                component="iframe"
                                src={supportFormUrl}
                                title="Sales and customer support form"
                                onLoad={() => setIframeLoading(false)}
                                className="report-issue-iframe"
                            />
                        </Box>
                        {/*<Typography variant="caption" color="text.secondary" className="report-issue-support-fallback">
                            If the form does not load here, open it in a new tab:{' '}
                            <Link href={SUPPORT_FORM_URL} target="_blank" rel="noreferrer">
                                customer support form
                            </Link>
                            .
                        </Typography>*/}
                    </Stack>
                </DialogContent>
            </Dialog>
            {showContent && selectingScreenshot && (
                <Portal>
                    <Box className="report-issue-selection-overlay" onMouseDown={handleMouseDown}>
                        <Box
                            className="report-issue-selection-toolbar"
                            onMouseDown={(event) => event.stopPropagation()}
                            onClick={(event) => event.stopPropagation()}
                        >
                            <Typography variant="body2">Select the area to attach.</Typography>
                            <Box className="report-issue-selection-actions">
                                <Button variant="outlined" size="small" onClick={handleCancelAreaSelection}>
                                    Cancel
                                </Button>
                                <Button
                                    variant="contained"
                                    size="small"
                                    onClick={() => void captureSelection()}
                                    disabled={!selectionRect || selectionRect.width < 8 || selectionRect.height < 8}
                                >
                                    Use screenshot
                                </Button>
                            </Box>
                        </Box>
                        {selectionRect && (
                            <Box
                                className="report-issue-selection-rect"
                                onMouseDown={(event) => event.stopPropagation()}
                                sx={{
                                    left: `${selectionRect.x}px`,
                                    top: `${selectionRect.y}px`,
                                    width: `${selectionRect.width}px`,
                                    height: `${selectionRect.height}px`,
                                }}
                            />
                        )}
                    </Box>
                </Portal>
            )}
        </>
    );
};

export default ReportIssue;
